// src/components/MobileTabBar.js
//
// THE MOBILE SHELL NAV (under md). VerticalNavbar is `hidden` at this
// width; this replaces it. See PLAN.md P9 / M0 for why a tab bar and not a
// drawer — the short version is that `useUnreadChats` drives a realtime
// badge, and a badge inside a hamburger is a notification nobody sees.
//
// FOUR TABS + More, because a bar holds five and the nav has up to nine
// items. The nine are not nine peers, so the cut is by KIND, not by
// importance:
//
//   Discover    a destination
//   Chats       a destination, and the one carrying the unread badge
//   Connect ·   the ACCOUNT slot — the wallet prompt until there is a
//   Profile     wallet, the profile once there is. Never both, so the bar
//               keeps one shape in either state.
//   More        everything else
//
// What More holds and why it is not a tab:
//   · Rewards — a destination, but a shallow one: a referral code and a
//     leaderboard, read occasionally, not a place anyone works from
//   · How it works — read once, then never again
//   · Create Model — a conversion ACTION that happens to live in a nav
//   · Studio / My Agent — a different MODE, only for approved creators,
//     and putting them in the bar would make the bar change shape when a
//     creator is approved
//   · Post (032-04) — the navbar's lime "Post" ACTION for approved creators:
//     a button opening the post composer (the profile has no "+" any more)
//
// Connect BREAKS the rule above — it is an action, and actions belong in
// More. The exception is deliberate: `setShowWalletModal` is reachable from
// VerticalNavbar, which is `hidden` under md, so before this a disconnected
// phone user could not connect from the shell at all. Their only ways in
// were a Swap flow and the Rewards hero button, and Rewards just left the
// bar. A connect button behind a sheet is a conversion nobody finds.
//
// DELIBERATELY ABSENT: `Livestreams` and `Reels`. Neither is a page — the
// paths are `/discover?tab=live` and `/discover?tab=reels`, the same route
// with a query param — so as tabs they would be second buttons leading
// where Discover already goes. They belong as tabs inside Discover, which
// is where they are.
//
// The item definitions are IMPORTED from VerticalNavbar, never copied: one
// nav in two places is a nav that drifts.

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MoreHorizontal, X } from 'lucide-react';
import {
  primaryNavItems,
  creatorNavItems,
  secondaryNavItems,
  profileItem,
  connectItem,
  createModelItem,
  postItem,
} from './VerticalNavbar';
import PostComposer from './kolTabs/PostComposer';
import { useToast } from './Toast';
import { landAfterPost } from '../lib/feedScroll';
import { useUnreadChats } from '../hooks/useUnreadChats';
import { useWallet } from '../context/WalletContext';

/** Routes that own the whole screen. The bar costs ~56px + safe-area
 *  permanently, which a vertical video feed and a live room both want
 *  back. Hiding it here is the accepted trade recorded in P9 — and
 *  CreatorReelsRow already precedents fullscreen behaviour on this route.
 *
 *  The broadcast console is the same case with a sharper edge: it is a
 *  h-[100dvh] console whose bottom row is Go Live / End Stream, so the bar
 *  does not merely cost height — it sits on top of the control that ends a
 *  live broadcast. The ✕ in its own header is the way out of this route. */
const IMMERSIVE = [/^\/reel\//, /^\/studio\/live\//, /^\/answer-call$/];

export function isImmersiveRoute(pathname) {
  return IMMERSIVE.some((re) => re.test(pathname));
}

const TAB_BASE =
  'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 ' +
  'px-1 text-[10px] font-medium tracking-tight transition-colors';

const MobileTabBar = ({ currentUser, isApprovedCreator = false, isConnected = false, creatorProfile = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [postOpen, setPostOpen] = useState(false);
  const postCreatorId = creatorProfile?.creator_id || creatorProfile?.id || null;
  const canPost = isConnected && isApprovedCreator && !!postCreatorId;
  const [moreOpen, setMoreOpen] = useState(false);
  const unreadChats = useUnreadChats(currentUser?.id);
  const { setShowWalletModal } = useWallet();

  // Close the sheet on navigation — otherwise tapping a link inside it
  // leaves the sheet covering the page you just asked for.
  useEffect(() => { setMoreOpen(false); }, [location.pathname, location.search]);

  const tabs = useMemo(() => {
    const discover = primaryNavItems.find((i) => i.id === 'discover');
    const chats = secondaryNavItems.find((i) => i.id === 'chats');
    return [
      discover,
      chats,
      // Slot three is the ACCOUNT slot: the wallet prompt until there is a
      // wallet, the profile once there is. The two are never both relevant,
      // so the bar holds four tabs + More in either state rather than
      // growing a fifth when a wallet connects.
      ...(isConnected ? [profileItem] : [connectItem]),
    ].filter(Boolean);
  }, [isConnected]);

  const moreItems = useMemo(() => [
    ...(isConnected && isApprovedCreator ? creatorNavItems.filter((i) => !i.action) : []),
    // secondaryNavItems is ordered chats · rewards · docs, so this filter
    // keeps Rewards above How it works without restating the order here.
    ...secondaryNavItems.filter((i) => i.id === 'rewards' || i.id === 'docs'),
    // 031-04: Create Model only while the account is not an approved creator
    ...(isConnected && isApprovedCreator ? [] : [createModelItem]),
    // 032-04: the lime "Post" action (a button), last, for an approved creator
    ...(canPost ? [postItem] : []),
  ], [isConnected, isApprovedCreator, canPost]);

  if (isImmersiveRoute(location.pathname)) return null;

  const isActive = (path) => {
    const base = path.split('?')[0];
    return location.pathname === base || location.pathname.startsWith(`${base}/`);
  };

  return (
    <>
      {moreOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMoreOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* MORE SHEET — a bottom sheet, not a dropdown: it is within thumb
          reach of the button that opened it. */}
      {moreOpen && (
        <div
          role="dialog"
          aria-label="More"
          className="fixed inset-x-0 bottom-0 z-[51] rounded-t-[28px] border-t border-white/[0.08]
                     bg-[#141414] pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2 md:hidden"
        >
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/15" />
          <div className="flex items-center justify-between px-5 pb-2">
            <span className="text-[13px] font-semibold text-white">More</span>
            <button
              type="button"
              onClick={() => setMoreOpen(false)}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full text-white/50 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
          <nav className="flex flex-col px-2 pb-1">
            {moreItems.map((item) => {
              const Icon = item.icon;
              if (item.action === 'post') {
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { setMoreOpen(false); setPostOpen(true); }}
                    className="flex min-h-[52px] items-center gap-3 rounded-2xl px-4 text-left text-[15px] font-semibold text-[#A3E635] transition-colors hover:bg-[#A3E635]/10"
                  >
                    {Icon && <Icon size={18} className="shrink-0" />}
                    <span>{item.label}</span>
                  </button>
                );
              }
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex min-h-[52px] items-center gap-3 rounded-2xl px-4 text-[15px] transition-colors
                    ${item.highlight
                      ? 'font-semibold text-[#A3E635] hover:bg-[#A3E635]/10'
                      : 'text-white/80 hover:bg-white/[0.06]'}`}
                >
                  {Icon && <Icon size={18} className="shrink-0" />}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* THE BAR. pb uses the safe-area inset so the tabs sit above the
          home indicator rather than under it. */}
      <nav
        aria-label="Primary"
        data-mobile-tabbar=""
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-white/[0.06]
                   bg-[#0F0F0F]/95 backdrop-blur-xl
                   pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {tabs.map((item) => {
          const Icon = item.icon;

          // Connect opens the wallet modal, so it is a <button> with no
          // route and no active state. It sits at full opacity against its
          // /45 siblings: it is the one thing in the bar that is asking to
          // be tapped, and lime is a status colour here, never an action.
          if (item.action === 'connect') {
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setShowWalletModal(true)}
                className={`${TAB_BASE} text-white`}
              >
                <span className="relative">
                  {Icon && <Icon size={21} strokeWidth={2.1} />}
                </span>
                <span className="leading-none">{item.label}</span>
              </button>
            );
          }

          const active = isActive(item.path);
          const badge = item.id === 'chats' ? unreadChats : 0;
          return (
            <Link
              key={item.id}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              className={`${TAB_BASE} ${active ? 'text-white' : 'text-white/45'}`}
            >
              <span className="relative">
                {Icon && <Icon size={21} strokeWidth={active ? 2.3 : 1.9} />}
                {badge > 0 && (
                  <span
                    aria-label={`${badge} unread`}
                    className="absolute -right-2 -top-1 flex h-[15px] min-w-[15px] items-center justify-center
                               rounded-full bg-[#A3E635] px-1 text-[9px] font-bold text-black"
                  >
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </span>
              <span className="leading-none">{item.label}</span>
              {active && (
                <span className="absolute inset-x-4 top-0 h-[2px] rounded-full bg-[#A3E635]" />
              )}
            </Link>
          );
        })}

        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          aria-label="More"
          className={`${TAB_BASE} ${moreOpen ? 'text-white' : 'text-white/45'}`}
        >
          <MoreHorizontal size={21} strokeWidth={moreOpen ? 2.3 : 1.9} />
          <span className="leading-none">More</span>
        </button>
      </nav>

      {canPost && (
        <PostComposer
          open={postOpen}
          onClose={() => setPostOpen(false)}
          creatorId={postCreatorId}
          onPosted={() => {
            showToast({ type: 'success', title: 'Post published!' });
            landAfterPost({ creatorProfile, location, navigate });
          }}
        />
      )}
    </>
  );
};

export default MobileTabBar;
